/**
 * My Workspaces page — /workspaces
 *
 * Shows only the workspaces owned by / created by the current user.
 * Topic/Department workspaces live at /workspaces/topics.
 */
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { usePageTitle } from '@/hooks/usePageTitle';
import { Button } from '@/components/ui/button';
import { useWorkspaces } from '@/hooks/useWorkspaces';
import { useWorkspaceDetail } from '@/hooks/useWorkspaceDetail';
import { useUser } from '@/hooks/useUser';
import type { Workspace } from '@/types/database';
import {
  NewWorkspaceWizard,
  type WizardResult,
} from '@/components/workspaces/NewWorkspaceWizard';
import {
  Plus,
  X,
  ExternalLink,
  Briefcase,
  Calendar,
  Hash,
  MapPin,
  Database,
  MessageSquare,
  ChevronDown,
  ChevronUp,
  Trash2,
  AlertTriangle,
  EyeOff,
  Eye,
  CheckSquare,
  Square,
} from 'lucide-react';

// ── Helpers ───────────────────────────────────────────────────────────────────

const ACCENT: Record<
  string,
  { bg: string; text: string; ring: string; dot: string }
> = {
  blue: {
    bg: 'bg-blue-50',
    text: 'text-blue-700',
    ring: 'ring-blue-200',
    dot: 'bg-blue-500',
  },
  violet: {
    bg: 'bg-violet-50',
    text: 'text-violet-700',
    ring: 'ring-violet-200',
    dot: 'bg-violet-500',
  },
  teal: {
    bg: 'bg-teal-50',
    text: 'text-teal-700',
    ring: 'ring-teal-200',
    dot: 'bg-teal-500',
  },
  amber: {
    bg: 'bg-amber-50',
    text: 'text-amber-700',
    ring: 'ring-amber-200',
    dot: 'bg-amber-500',
  },
  slate: {
    bg: 'bg-slate-100',
    text: 'text-slate-600',
    ring: 'ring-slate-200',
    dot: 'bg-slate-400',
  },
};

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, { label: string; cls: string }> = {
    active: {
      label: 'Active',
      cls: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    },
    inactive: {
      label: 'Inactive',
      cls: 'bg-gray-100 text-gray-500 border-gray-200',
    },
    draft: {
      label: 'Draft',
      cls: 'bg-amber-100 text-amber-700 border-amber-200',
    },
    archived: {
      label: 'Archived',
      cls: 'bg-slate-100 text-slate-500 border-slate-200',
    },
  };
  const cfg = map[status] ?? {
    label: status,
    cls: 'bg-gray-100 text-gray-500 border-gray-200',
  };
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${cfg.cls}`}
    >
      {cfg.label}
    </span>
  );
}

function TagPill({ label, color }: { label: string; color: string }) {
  const c = ACCENT[color] ?? ACCENT.slate;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${c.bg} ${c.text} ${c.ring}`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${c.dot}`} />
      {label}
    </span>
  );
}

// ── Slide-out panel ───────────────────────────────────────────────────────────

function SlideOut({
  workspace,
  onClose,
  onNavigate,
  onAsk,
  onHide,
  onUnhide,
}: {
  workspace: Workspace | null;
  onClose: () => void;
  onNavigate: (id: string) => void;
  onAsk: (id: string, question?: string) => void;
  onHide: (id: string) => void;
  onUnhide?: (id: string) => void;
}) {
  const [showAdmin, setShowAdmin] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const { deleteWorkspace } = useWorkspaces();
  const { user } = useUser();
  const canDelete =
    user?.role === 'SYSTEM_ADMIN' ||
    user?.role === 'ORG_OWNER' ||
    user?.role === 'WORKSPACE_ADMIN' ||
    workspace?.owner_user_id === user?.id;
  const { data, loading: detailLoading } = useWorkspaceDetail(
    workspace?.id ?? undefined
  );

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    if (workspace) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [workspace, onClose]);

  const fmt = (s: string | null | undefined) =>
    s
      ? new Date(s).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        })
      : '—';

  const fmtMonth = (s: string | null | undefined) =>
    s
      ? new Date(s).toLocaleDateString('en-US', {
          month: 'short',
          year: 'numeric',
        })
      : '—';

  const isVisible = !!workspace;
  const jurisdictions = data?.jurisdictions ?? [];
  const stats = data?.quick_stats;

  return (
    <>
      <div
        className={`fixed inset-0 z-30 bg-black/20 transition-opacity duration-300 ${
          isVisible ? 'opacity-100' : 'pointer-events-none opacity-0'
        }`}
      />
      <div
        ref={ref}
        className={`fixed right-0 top-0 z-40 flex h-full w-[460px] flex-col bg-white shadow-2xl transition-transform duration-300 ease-in-out ${
          isVisible ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {workspace && (
          <>
            {/* ── Header ─────────────────────────────────────────── */}
            <div className="flex items-start justify-between border-b border-gray-200 px-6 py-5">
              <div className="flex min-w-0 items-center gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-100">
                  <Briefcase className="h-5 w-5 text-blue-600" />
                </div>
                <div className="min-w-0">
                  <p className="truncate text-base font-bold text-gray-900">
                    {workspace.name}
                  </p>
                  <div className="mt-0.5 flex items-center gap-1.5 flex-wrap">
                    <StatusBadge status={workspace.status} />
                    {workspace.published_to_org && (
                      <TagPill label="Org-wide" color="teal" />
                    )}
                  </div>
                </div>
              </div>
              <button
                onClick={onClose}
                className="ml-4 mt-0.5 shrink-0 rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* ── Body ───────────────────────────────────────────── */}
            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
              {/* Description */}
              {workspace.description && (
                <p className="text-sm text-gray-700 leading-relaxed">
                  {workspace.description}
                </p>
              )}

              {/* ── Places ─────────────────────────────────────── */}
              <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                <p className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-widest text-gray-400">
                  <MapPin className="h-3.5 w-3.5" /> Places
                </p>
                {detailLoading ? (
                  <div className="space-y-1.5 animate-pulse">
                    <div className="h-3 w-2/3 rounded bg-gray-200" />
                    <div className="h-3 w-1/2 rounded bg-gray-200" />
                  </div>
                ) : jurisdictions.length === 0 ? (
                  <p className="text-sm text-gray-400">No places configured</p>
                ) : (
                  <ul className="space-y-1.5">
                    {jurisdictions.map((j) => (
                      <li
                        key={j.id}
                        className="flex items-center gap-2 text-sm text-gray-800 font-medium"
                      >
                        <span className="h-2 w-2 rounded-full bg-blue-500 flex-shrink-0" />
                        {j.name}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* ── Corpus stats ───────────────────────────────── */}
              <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                <p className="mb-3 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-widest text-gray-400">
                  <Database className="h-3.5 w-3.5" /> Document Archive
                </p>
                {detailLoading ? (
                  <div className="grid grid-cols-2 gap-3 animate-pulse">
                    {[1, 2, 3, 4].map((i) => (
                      <div key={i} className="h-10 rounded bg-gray-200" />
                    ))}
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      {
                        label: 'Documents',
                        value: stats?.total_docs?.toLocaleString() ?? '—',
                      },
                      {
                        label: 'Places',
                        value:
                          jurisdictions.length === 1
                            ? '1 place'
                            : `${jurisdictions.length} places`,
                      },
                      {
                        label: 'Coverage from',
                        value: fmtMonth(stats?.coverage_from),
                      },
                      {
                        label: 'Last ingested',
                        value: fmt(stats?.last_ingested_at),
                      },
                    ].map(({ label, value }) => (
                      <div
                        key={label}
                        className="rounded-lg bg-white border border-gray-200 px-3 py-2.5"
                      >
                        <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                          {label}
                        </p>
                        <p className="mt-0.5 text-sm font-bold text-gray-900">
                          {value}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* ── Coverage period ────────────────────────────── */}
              {!detailLoading && stats?.coverage_from && (
                <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                  <p className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-widest text-gray-400">
                    <Calendar className="h-3.5 w-3.5" /> Coverage Period
                  </p>
                  <p className="text-sm font-medium text-gray-800">
                    {fmtMonth(stats.coverage_from)} —{' '}
                    {stats.coverage_to ? fmt(stats.coverage_to) : 'present'}
                  </p>
                  <p className="mt-0.5 text-xs text-gray-500">
                    Date range of {stats.dated_docs?.toLocaleString() ?? 0}{' '}
                    dated document
                    {stats.dated_docs !== 1 ? 's' : ''}
                    {stats.total_docs != null &&
                      stats.dated_docs != null &&
                      stats.dated_docs < stats.total_docs &&
                      ` (of ${stats.total_docs.toLocaleString()} total)`}
                  </p>
                </div>
              )}

              {/* ── Suggested questions ────────────────────────── */}
              {!detailLoading &&
                (data?.suggested_questions ?? []).length > 0 && (
                  <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                    <p className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-widest text-gray-400">
                      <MessageSquare className="h-3.5 w-3.5" /> Ask the archive
                    </p>
                    <ul className="space-y-1">
                      {(data?.suggested_questions ?? [])
                        .slice(0, 3)
                        .map((q, i) => (
                          <li key={i}>
                            <button
                              type="button"
                              onClick={() => onAsk(workspace.id, q)}
                              className="w-full text-left flex items-start gap-2 rounded-lg px-2 py-1.5
                                     text-sm text-gray-700 hover:bg-white hover:text-gray-900
                                     transition-colors group"
                            >
                              <span className="text-gray-300 group-hover:text-blue-400 flex-shrink-0 mt-px transition-colors">
                                ✦
                              </span>
                              <span className="leading-snug">{q}</span>
                            </button>
                          </li>
                        ))}
                    </ul>
                  </div>
                )}

              {/* ── Admin details (collapsed) ───────────────────── */}
              <div className="rounded-xl border border-gray-200 overflow-hidden">
                <button
                  type="button"
                  onClick={() => setShowAdmin((v) => !v)}
                  className="w-full flex items-center justify-between px-4 py-3 text-[11px] font-semibold uppercase tracking-widest text-gray-400 hover:bg-gray-50 transition-colors"
                >
                  <span className="flex items-center gap-1.5">
                    <Hash className="h-3.5 w-3.5" /> Technical Details
                  </span>
                  {showAdmin ? (
                    <ChevronUp className="h-3.5 w-3.5" />
                  ) : (
                    <ChevronDown className="h-3.5 w-3.5" />
                  )}
                </button>
                {showAdmin && (
                  <div className="divide-y divide-gray-100 border-t border-gray-100 bg-gray-50">
                    {[
                      {
                        label: 'Workspace ID',
                        value: workspace.id,
                        mono: true,
                      },
                      { label: 'Scope', value: workspace.scope, mono: false },
                      { label: 'Kind', value: workspace.kind, mono: false },
                      {
                        label: 'Created',
                        value: fmt(workspace.created_at),
                        mono: false,
                      },
                      {
                        label: 'Updated',
                        value: fmt(workspace.updated_at),
                        mono: false,
                      },
                      ...(workspace.created_via
                        ? [
                            {
                              label: 'Created via',
                              value: workspace.created_via,
                              mono: false,
                            },
                          ]
                        : []),
                      ...(workspace.jurisdiction_id
                        ? [
                            {
                              label: 'Jurisdiction ID',
                              value: workspace.jurisdiction_id,
                              mono: true,
                            },
                          ]
                        : []),
                    ].map(({ label, value, mono }) => (
                      <div
                        key={label}
                        className="flex items-start gap-3 px-4 py-2.5"
                      >
                        <div className="min-w-0 flex-1">
                          <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                            {label}
                          </p>
                          <p
                            className={`mt-0.5 text-xs text-gray-600 break-all ${mono ? 'font-mono' : ''}`}
                          >
                            {value}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* ── Footer actions ─────────────────────────────────── */}
            <div className="border-t border-gray-200 px-6 py-4 flex flex-col gap-2">
              <Button
                className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                onClick={() => onAsk(workspace.id)}
              >
                <MessageSquare className="mr-2 h-4 w-4" />
                Ask the Archive
              </Button>
              <Button
                variant="outline"
                className="w-full"
                onClick={() => onNavigate(workspace.id)}
              >
                <ExternalLink className="mr-2 h-4 w-4" />
                Open Workspace
              </Button>

              {canDelete &&
                (!deleteConfirm ? (
                  <Button
                    variant="ghost"
                    className="w-full text-red-600 hover:bg-red-50 hover:text-red-700"
                    onClick={() => setDeleteConfirm(true)}
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete Workspace
                  </Button>
                ) : (
                  <div className="space-y-2 rounded-lg border border-red-200 bg-red-50 p-3">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-500" />
                      <div className="text-xs text-red-700">
                        <p className="font-semibold">Delete this workspace?</p>
                        <p className="mt-0.5 text-red-600">
                          This will archive the workspace. It can be restored by
                          an admin if needed.
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className="flex-1 text-xs"
                        onClick={() => setDeleteConfirm(false)}
                        disabled={deleting}
                      >
                        Cancel
                      </Button>
                      <Button
                        size="sm"
                        className="flex-1 bg-red-600 text-xs hover:bg-red-700"
                        disabled={deleting}
                        onClick={async () => {
                          setDeleting(true);
                          try {
                            await deleteWorkspace(workspace.id);
                            onClose();
                          } catch {
                            setDeleting(false);
                          }
                        }}
                      >
                        {deleting ? 'Deleting…' : 'Confirm Delete'}
                      </Button>
                    </div>
                  </div>
                ))}

              {!canDelete && (
                <Button
                  variant="ghost"
                  className="w-full text-gray-500 hover:bg-gray-50 hover:text-gray-700"
                  onClick={() => {
                    onHide(workspace.id);
                    onClose();
                  }}
                >
                  <EyeOff className="mr-2 h-4 w-4" />
                  Remove from My View
                </Button>
              )}

              {onUnhide && (
                <Button
                  variant="ghost"
                  className="w-full text-blue-600 hover:bg-blue-50 hover:text-blue-700"
                  onClick={() => {
                    onUnhide(workspace.id);
                    onClose();
                  }}
                >
                  <Eye className="mr-2 h-4 w-4" />
                  Restore to My View
                </Button>
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
}

// ── Workspace row ─────────────────────────────────────────────────────────────

function WorkspaceRow({
  ws,
  isSelected,
  onSelect,
  isChecked,
  onToggleCheck,
}: {
  ws: Workspace;
  isSelected: boolean;
  onSelect: (ws: Workspace) => void;
  isChecked: boolean;
  onToggleCheck: (id: string) => void;
}) {
  return (
    <div
      className={`grid w-full grid-cols-[auto_1fr_auto_auto] items-center gap-x-3 border-b border-gray-50 last:border-0 py-2.5 pr-5 text-left transition-colors
        ${isSelected ? 'bg-blue-50' : 'hover:bg-slate-50'}`}
    >
      {/* Checkbox */}
      <div className="pl-4">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onToggleCheck(ws.id);
          }}
          className="rounded p-0.5 text-gray-400 hover:text-blue-600 transition-colors"
        >
          {isChecked ? (
            <CheckSquare className="h-4 w-4 text-blue-600" />
          ) : (
            <Square className="h-4 w-4" />
          )}
        </button>
      </div>

      {/* Click target for slide-out */}
      <button
        type="button"
        onClick={() => onSelect(ws)}
        className="min-w-0 text-left"
      >
        <p className="truncate text-sm font-medium text-gray-800">{ws.name}</p>
        <p className="mt-0.5 font-mono text-[10px] text-gray-300 truncate">
          {ws.id} ·{' '}
          {new Date(ws.created_at).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          })}
        </p>
      </button>
      <div className="flex items-center gap-1.5">
        {ws.scope === 'platform' && <TagPill label="Platform" color="blue" />}
        {ws.kind === 'themed' && <TagPill label="Themed" color="violet" />}
        {ws.kind === 'jurisdiction' && (
          <TagPill label="Jurisdiction" color="teal" />
        )}
        {ws.published_to_org && <TagPill label="Org-wide" color="amber" />}
      </div>
      <StatusBadge status={ws.status} />
    </div>
  );
}

// ── Empty state ───────────────────────────────────────────────────────────────

function EmptyState({ onCreate }: { onCreate: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-200 py-20 text-center">
      <Briefcase className="mb-4 h-12 w-12 text-gray-300" />
      <h3 className="mb-1 text-base font-semibold text-gray-700">
        No workspaces yet
      </h3>
      <p className="mb-6 text-sm text-gray-400">
        Create a workspace to save a jurisdiction + theme + date scope.
      </p>
      <Button
        onClick={onCreate}
        className="bg-blue-600 hover:bg-blue-700 text-white"
      >
        <Plus className="mr-2 h-4 w-4" />
        New Workspace
      </Button>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export const WorkspacesPage: React.FC = () => {
  usePageTitle('My Workspaces');
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    workspaces,
    loading,
    error: workspacesError,
    fetchWorkspaces,
    setCurrentWorkspace,
    createWorkspace,
  } = useWorkspaces();
  const { user } = useUser();

  const STORAGE_KEY = 'workspaceHiddenIds';

  const [hiddenIds, setHiddenIds] = useState<Set<string>>(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? new Set(JSON.parse(raw) as string[]) : new Set();
    } catch {
      return new Set();
    }
  });

  const [showHidden, setShowHidden] = useState(false);
  const [checkedIds, setCheckedIds] = useState<Set<string>>(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState(false);

  const persistHidden = (next: Set<string>) => {
    setHiddenIds(next);
    localStorage.setItem(STORAGE_KEY, JSON.stringify([...next]));
  };

  const handleHideWorkspace = (id: string) => {
    const next = new Set(hiddenIds);
    next.add(id);
    persistHidden(next);
  };

  const handleUnhideWorkspace = (id: string) => {
    const next = new Set(hiddenIds);
    next.delete(id);
    persistHidden(next);
  };

  const handleToggleCheck = (id: string) => {
    setCheckedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleToggleAll = () => {
    if (checkedIds.size === myWorkspaces.length) {
      setCheckedIds(new Set());
    } else {
      setCheckedIds(new Set(myWorkspaces.map((w) => w.id)));
    }
  };

  const handleBulkHide = () => {
    const next = new Set(hiddenIds);
    for (const id of checkedIds) next.add(id);
    persistHidden(next);
    setCheckedIds(new Set());
  };

  const { deleteWorkspace } = useWorkspaces();
  const handleBulkDelete = async () => {
    setBulkDeleting(true);
    try {
      for (const id of checkedIds) {
        await deleteWorkspace(id);
      }
      setCheckedIds(new Set());
    } catch {
      /* error handled in context */
    } finally {
      setBulkDeleting(false);
      setBulkDeleteConfirm(false);
    }
  };

  const ownsWorkspace = (ws: Workspace) =>
    ws.owner_user_id === user?.id ||
    user?.role === 'SYSTEM_ADMIN' ||
    user?.role === 'ORG_OWNER' ||
    user?.role === 'WORKSPACE_ADMIN';

  const checkedDelete = useMemo(
    () =>
      [...checkedIds].filter((id) => {
        const ws = workspaces.find((w) => w.id === id);
        return ws && ownsWorkspace(ws);
      }),
    [checkedIds, workspaces, user]
  );

  const checkedHide = useMemo(
    () =>
      [...checkedIds].filter(
        (id) => !ownsWorkspace(workspaces.find((w) => w.id === id)!)
      ),
    [checkedIds, workspaces, user]
  );

  const visibleWorkspaces = useMemo(
    () => workspaces.filter((w) => !hiddenIds.has(w.id)),
    [workspaces, hiddenIds]
  );

  const hiddenWorkspaces = useMemo(
    () => workspaces.filter((w) => hiddenIds.has(w.id)),
    [workspaces, hiddenIds]
  );

  // Org-visible model: all org members see all workspaces.
  // We still show the list with a "created by" indicator so users can find their own.
  // No filter by owner_user_id here — that's stored in the DB for display purposes only.
  const myWorkspaces = useMemo(() => visibleWorkspaces, [visibleWorkspaces]);

  // Safety valve: if loading hangs for >12 s, trigger a retry automatically
  useEffect(() => {
    if (!loading) return;
    const t = setTimeout(() => void fetchWorkspaces(), 12000);
    return () => clearTimeout(t);
  }, [loading, fetchWorkspaces]);

  const [showWizard, setShowWizard] = useState(false);
  const [selectedWs, setSelectedWs] = useState<Workspace | null>(null);

  // Open wizard directly when navigated via Create Workspace button
  useEffect(() => {
    if (searchParams.get('new') === 'true') {
      setShowWizard(true);
      // Clean URL param so back-navigation doesn't re-trigger
      const next = new URLSearchParams(searchParams);
      next.delete('new');
      setSearchParams(next, { replace: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleWizardDone = async (result: WizardResult) => {
    const workspace = await createWorkspace({
      name: result.name,
      description: result.description || undefined,
      kind: 'custom',
      jurisdiction_ids: result.jurisdictionIds,
      date_from: result.dateFrom ?? null,
      date_to: result.dateTo ?? null,
      include_descendants: true,
    });
    setShowWizard(false);
    navigate(`/workspaces/${workspace.id}`);
  };

  const handleNavigate = (id: string) => {
    const ws = workspaces.find((w) => w.id === id);
    if (ws) setCurrentWorkspace(ws);
    setSelectedWs(null);
    navigate(`/workspaces/${id}`);
  };

  const handleAsk = (id: string, question?: string) => {
    setSelectedWs(null);
    navigate(`/tools/on-the-record?workspace=${id}`, {
      state: {
        preselectedWorkspaceId: id,
        ...(question ? { prefillQuestion: question } : {}),
      },
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <div className="mx-auto mb-4 h-10 w-10 animate-spin rounded-full border-4 border-blue-600 border-t-transparent" />
          <p className="text-sm text-gray-500">Loading workspaces…</p>
        </div>
      </div>
    );
  }

  if (workspacesError) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-6 py-5 max-w-md">
          <p className="mb-1 text-sm font-semibold text-red-700">
            Failed to load workspaces
          </p>
          <p className="text-xs text-red-500">{workspacesError}</p>
        </div>
        <Button
          onClick={() => void fetchWorkspaces()}
          className="bg-blue-600 hover:bg-blue-700 text-white"
        >
          Retry
        </Button>
      </div>
    );
  }

  // ── Wizard view ──
  if (showWizard) {
    return (
      <NewWorkspaceWizard
        onDone={handleWizardDone}
        onCancel={() => setShowWizard(false)}
      />
    );
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">My Workspaces</h1>
          <p className="mt-0.5 text-sm text-gray-500">
            Workspaces you own or created
          </p>
        </div>
        <Button
          onClick={() => setShowWizard(true)}
          className="bg-blue-600 hover:bg-blue-700 text-white"
        >
          <Plus className="mr-2 h-4 w-4" />
          New Workspace
        </Button>
      </div>

      {/* List */}
      {myWorkspaces.length === 0 && hiddenWorkspaces.length === 0 ? (
        <EmptyState onCreate={() => setShowWizard(true)} />
      ) : (
        <>
          {myWorkspaces.length > 0 && (
            <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
              {/* Header */}
              <div className="grid grid-cols-[auto_1fr_auto_auto] items-center gap-x-3 border-b border-gray-100 bg-gray-50 py-2 text-xs font-semibold uppercase tracking-wide text-gray-400">
                <span className="pl-4">
                  <button
                    type="button"
                    onClick={handleToggleAll}
                    className="rounded p-0.5 text-gray-400 hover:text-blue-600 transition-colors"
                  >
                    {checkedIds.size === myWorkspaces.length &&
                    myWorkspaces.length > 0 ? (
                      <CheckSquare className="h-4 w-4 text-blue-600" />
                    ) : (
                      <Square className="h-4 w-4" />
                    )}
                  </button>
                </span>
                <span>Workspace</span>
                <span className="text-right">Tags</span>
                <span className="text-right pr-5">Status</span>
              </div>
              {/* Bulk action bar */}
              {checkedIds.size > 0 && !bulkDeleteConfirm && (
                <div className="flex items-center gap-2 border-b border-blue-100 bg-blue-50 px-4 py-2">
                  <span className="text-xs font-medium text-blue-700">
                    {checkedIds.size} selected
                  </span>
                  <div className="flex-1" />
                  {checkedHide.length > 0 && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-xs"
                      onClick={handleBulkHide}
                    >
                      <EyeOff className="mr-1.5 h-3 w-3" />
                      Hide {checkedHide.length}
                    </Button>
                  )}
                  {checkedDelete.length > 0 && (
                    <Button
                      size="sm"
                      className="h-7 text-xs bg-red-600 hover:bg-red-700"
                      onClick={() => setBulkDeleteConfirm(true)}
                    >
                      <Trash2 className="mr-1.5 h-3 w-3" />
                      Delete {checkedDelete.length}
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 text-xs"
                    onClick={() => {
                      setCheckedIds(new Set());
                      setBulkDeleteConfirm(false);
                    }}
                  >
                    Clear
                  </Button>
                </div>
              )}

              {/* Bulk delete confirmation */}
              {bulkDeleteConfirm && (
                <div className="flex items-center gap-3 border-b border-red-200 bg-red-50 px-4 py-2.5">
                  <AlertTriangle className="h-4 w-4 flex-shrink-0 text-red-500" />
                  <span className="text-sm font-medium text-red-800">
                    Delete {checkedDelete.length} workspace
                    {checkedDelete.length !== 1 ? 's' : ''}?
                  </span>
                  <span className="text-xs text-red-600">
                    This will archive{' '}
                    {checkedDelete.length === 1 ? 'it' : 'them'}. An admin can
                    restore if needed.
                  </span>
                  <div className="flex-1" />
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 text-xs"
                    onClick={() => setBulkDeleteConfirm(false)}
                    disabled={bulkDeleting}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    className="h-7 text-xs bg-red-600 hover:bg-red-700"
                    onClick={handleBulkDelete}
                    disabled={bulkDeleting}
                  >
                    {bulkDeleting
                      ? 'Deleting…'
                      : `Yes, Delete ${checkedDelete.length}`}
                  </Button>
                </div>
              )}
              {myWorkspaces.map((ws) => (
                <WorkspaceRow
                  key={ws.id}
                  ws={ws}
                  isSelected={selectedWs?.id === ws.id}
                  onSelect={setSelectedWs}
                  isChecked={checkedIds.has(ws.id)}
                  onToggleCheck={handleToggleCheck}
                />
              ))}
            </div>
          )}

          {hiddenWorkspaces.length > 0 && (
            <div className="border-t border-gray-200 pt-3">
              <button
                type="button"
                onClick={() => setShowHidden((v) => !v)}
                className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
              >
                {showHidden ? (
                  <EyeOff className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
                {showHidden
                  ? `Hide ${hiddenWorkspaces.length} hidden workspace${hiddenWorkspaces.length !== 1 ? 's' : ''}`
                  : `Show ${hiddenWorkspaces.length} hidden workspace${hiddenWorkspaces.length !== 1 ? 's' : ''}`}
              </button>
              {showHidden && (
                <div className="mt-2 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
                  {hiddenWorkspaces.map((ws) => (
                    <WorkspaceRow
                      key={ws.id}
                      ws={ws}
                      isSelected={selectedWs?.id === ws.id}
                      onSelect={setSelectedWs}
                      isChecked={checkedIds.has(ws.id)}
                      onToggleCheck={handleToggleCheck}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Slide-out */}
      <SlideOut
        workspace={selectedWs}
        onClose={() => setSelectedWs(null)}
        onNavigate={handleNavigate}
        onAsk={handleAsk}
        onHide={handleHideWorkspace}
        onUnhide={
          selectedWs && hiddenIds.has(selectedWs.id)
            ? handleUnhideWorkspace
            : undefined
        }
      />
    </div>
  );
};
